import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
    MdClose,
    MdExitToApp,
    MdKeyboardArrowDown,
    MdKeyboardArrowUp,
    MdOutlineBookmark,
    MdOutlineBookmarkBorder,
    MdOutlineKeyboard,
    MdOutlineLocationOn,
    // MdOutlinePlayCircle
} from 'react-icons/md';
import moment from 'moment';
// import Player from '@vimeo/player';
import Button from 'components/Button';
import Loader from 'components/Loader';
import Popup from 'components/Popup';
import axios from 'config/axios';
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import { setError } from 'store/error';
import { MULTIPLE_CHOICES } from 'global/constants';
import { getDeviceOS } from 'global/os';
import useOutsideClickHandler from 'hooks/useOutsideClickHandler';
import Shortcuts from '../Module/Shortcuts';
import FigureViewer from '../FigureViewer';
import ToolErrorBoundary from '../ToolErrorBoundary';
import './index.css';

import 'katex/dist/katex.min.css';
import renderMathInElement from "katex/dist/contrib/auto-render.js";
import { flattenKatexUnderlines } from 'global/katexWrap';

function DigitalBCAReview() {
    const [studentName, setStudentName] = useState('');
    const [testModules, setTestModules] = useState(null);
    const [moduleIndex, setModuleIndex] = useState(0);
    const [questionIndex, setQuestionIndex] = useState(0);
    const [moduleSelectOpen, setModuleSelectOpen] = useState(false);
    const [directionsOpen, setDirectionsOpen] = useState(false);
    const [navigatorOpen, setNavigatorOpen] = useState(false);
    const [shortcutsOpen, setShortcutsOpen] = useState(false);
    const [showAnswer, setShowAnswer] = useState(false);
    const [shortcutKeyPressed, setShortcutKeyPressed] = useState(false);
    // const [player, setPlayer] = useState(null);
    const [explanationOpen, setExplanationOpen] = useState(false);

    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { organization } = useAppSelector(state => state.organization);
    const { testId } = useParams();
    const moduleSelectRef = useOutsideClickHandler(() => setTimeout(() => setModuleSelectOpen(false), 10));
    const directionsRef = useOutsideClickHandler(() => setTimeout(() => setDirectionsOpen(false), 10));
    const navigatorRef = useOutsideClickHandler(() => setTimeout(() => setNavigatorOpen(false), 10));
    // const explanationRef = useOutsideClickHandler(() => setExplanationVideo(null));

    const prevQuestion = () => {
        if (questionIndex > 0) {
            setQuestionIndex(questionIndex - 1);
        }
        else if (moduleIndex > 0) {
            setQuestionIndex(testModules[moduleIndex - 1].questions.length - 1);
            setModuleIndex(moduleIndex - 1);
        }
    }

    const nextQuestion = () => {
        if (questionIndex < testModules[moduleIndex].questions.length - 1) {
            setQuestionIndex(questionIndex + 1);
        }
        else if (moduleIndex < testModules.length - 1) {
            setModuleIndex(moduleIndex + 1);
            setQuestionIndex(0);
        }
        else {
            // exit the review
            navigate(-1);
        }
    }

    const goToQuestion = (index: number) => {
        setQuestionIndex(index);

        let question = document.getElementById('bcaReview__questionChoices');

        if (question) question.scrollTop = 0;
    }

    const formatTime = (time: number) => {
        const duration = moment.duration(time, 'seconds');

        const hours = duration.hours();
        const minutes = (hours && duration.minutes() < 10) ? `0${duration.minutes()}` : duration.minutes();
        const seconds = duration.seconds() < 10 ? `0${duration.seconds()}` : duration.seconds();

        return hours ? `${hours}:${minutes}:${seconds}` : `${minutes}:${seconds}`;
    }

    const renderKatex = () => {
        const katex = document.getElementsByClassName('katex');
        for (let i = 0; i < katex.length; i++) {
            renderMathInElement(katex[i], {
                delimiters: [
                    {left: '$$', right: '$$', display: true},
                    {left: '$', right: '$', display: false},
                    {left: '\\(', right: '\\)', display: false},
                    {left: '\\[', right: '\\]', display: true}
                ],
                trust: true
            });

            katex[i].innerHTML = katex[i].innerHTML.replaceAll('&nbsp;', ' ');
        }

        const wrappingTexts = document.querySelectorAll<HTMLElement>('.katex-html .base');
        for (let i = 0; i < wrappingTexts.length; i++) {
            // console.log(wrappingTexts[i]);
            if (testModules[moduleIndex].subject === 'Reading and Writing') {
                wrappingTexts[i].classList.add('wrap');
            }
            else {
                wrappingTexts[i].classList.remove('wrap');
            }
        }

        flattenKatexUnderlines(document);

        const math = document.querySelectorAll('.katex-mathml');
        for (let i = 0; i < math.length; i++) {
            math[i].remove();
        }
    }

    const handleWindowsShortcutKeys = (event: KeyboardEvent) => {
        const {
            ctrlKey,
            altKey,
            shiftKey,
            code
        } = event;

        if (ctrlKey && altKey && shiftKey) {
            switch(code) {
                case 'KeyD':
                    setDirectionsOpen(!directionsOpen);
                    break;
                default:
                    return;
            }
        }
        else if (ctrlKey && altKey) {
            switch(code) {
                case 'KeyB':
                    prevQuestion();
                    break;
                case 'KeyX':
                    nextQuestion();
                    break;
                // case 'KeyT':
                //     setHideTimer(!hideTimer);
                //     break;
                // case 'KeyV':
                //     markQuestion();
                //     break;
                // case 'KeyA':
                //     if (testModules[moduleIndex].subject === 'Reading and Writing') {
                //         if (annotation) setAnnotation(null);
                //         if (shortcutsOpen) setShortcutsOpen(false);
                //         if (exitOpen) setExitOpen(false);
                //         if (annotation) setAnnotation(null);
                //         else selectAnnotation();
                //     }
                //     break;
                // case 'KeyM':
                //     if (questions[questionIndex].choices.length > 0) {
                //         setCrossOut(!crossOut);
                //     }
                //     break;
                // case 'Digit1':
                //     if (questions[questionIndex].choices.length > 0) {
                //         crossOutChoice(questions[questionIndex].choices[0].id);
                //     }
                //     break;
                // case 'Digit2':
                //     if (questions[questionIndex].choices.length > 0) {
                //         crossOutChoice(questions[questionIndex].choices[1].id);
                //     }
                //     break;
                // case 'Digit3':
                //     if (questions[questionIndex].choices.length > 0) {
                //         crossOutChoice(questions[questionIndex].choices[2].id);
                //     }
                //     break;
                // case 'Digit4':
                //     if (questions[questionIndex].choices.length > 0) {
                //         crossOutChoice(questions[questionIndex].choices[3].id);
                //     }
                //     break;
                default:
                    return;
            }
            
        }
        else if (ctrlKey && shiftKey) {
            switch(code) {
                // case 'Digit1':
                //     if (questions[questionIndex].choices.length > 0) {
                //         handleChange(questions[questionIndex].choices[0].id);
                //     }
                //     break;
                // case 'Digit2':
                //     if (questions[questionIndex].choices.length > 0) {
                //         handleChange(questions[questionIndex].choices[1].id);
                //     }
                //     break;
                // case 'Digit3':
                //     if (questions[questionIndex].choices.length > 0) {
                //         handleChange(questions[questionIndex].choices[2].id);
                //     }
                //     break;
                // case 'Digit4':
                //     if (questions[questionIndex].choices.length > 0) {
                //         handleChange(questions[questionIndex].choices[3].id);
                //     }
                //     break;
                default:
                    return;
            }
        }

        setShortcutKeyPressed(!shortcutKeyPressed);
    }

    const handleMacShortcutKeys = (event: KeyboardEvent) => {
        const {
            ctrlKey,
            altKey,
            shiftKey,
            metaKey,
            code
        } = event;

        if (metaKey && ctrlKey && shiftKey) {
            switch(code) {
                case 'KeyD':
                    setDirectionsOpen(!directionsOpen);
                    break;
                default:
                    return;
            }
        }
        else if (metaKey && ctrlKey) {
            switch(code) {
                case 'KeyB':
                    prevQuestion();
                    break;
                case 'KeyX':
                    nextQuestion();
                    break;
                // case 'Digit3':
                //     if (questions[questionIndex].choices.length > 0) {
                //         handleChange(questions[questionIndex].choices[0].id);
                //     }
                //     break;
                // case 'Digit4':
                //     if (questions[questionIndex].choices.length > 0) {
                //         handleChange(questions[questionIndex].choices[1].id);
                //     }
                //     break;
                // case 'Digit5':
                //     if (questions[questionIndex].choices.length > 0) {
                //         handleChange(questions[questionIndex].choices[2].id);
                //     }
                //     break;
                // case 'Digit6':
                //     if (questions[questionIndex].choices.length > 0) {
                //         handleChange(questions[questionIndex].choices[3].id);
                //     }
                //     break;
                // case 'Digit7':
                //     if (questions[questionIndex].choices.length > 0) {
                //         crossOutChoice(questions[questionIndex].choices[0].id);
                //     }
                //     break;
                // case 'Digit8':
                //     if (questions[questionIndex].choices.length > 0) {
                //         crossOutChoice(questions[questionIndex].choices[1].id);
                //     }
                //     break;
                // case 'Digit9':
                //     if (questions[questionIndex].choices.length > 0) {
                //         crossOutChoice(questions[questionIndex].choices[2].id);
                //     }
                //     break;
                // case 'Digit0':
                //     if (questions[questionIndex].choices.length > 0) {
                //         crossOutChoice(questions[questionIndex].choices[3].id);
                //     }
                //     break;
                default:
                    return;
            }
        }
        else if (metaKey && altKey) {
            switch(code) {
                // case 'KeyG':
                //     setHideTimer(!hideTimer);
                //     break;
                // case 'KeyA':
                //     if (testModules[moduleIndex].subject === 'Reading and Writing') {
                //         if (annotation) setAnnotation(null);
                //         if (shortcutsOpen) setShortcutsOpen(false);
                //         if (exitOpen) setExitOpen(false);
                //         if (annotation) setAnnotation(null);
                //         else selectAnnotation();
                //     }
                //     break;
                // case 'KeyM':
                //     if (questions[questionIndex].choices.length > 0) {
                //         setCrossOut(!crossOut);
                //     }
                //     break;
                default:
                    return;
            }
            
        }
        else if (metaKey && shiftKey) {
            switch(code) {
                // case 'KeyV':
                //     markQuestion();
                //     break;
                default:
                    return;
            }
        }

        setShortcutKeyPressed(!shortcutKeyPressed);
    }

    // const dragMove = (element: HTMLElement, header: HTMLElement) => {
    //     let pos1 = 0, pos2 = 0, pos3 = 0, pos4 = 0;

    //     const dragMouseDown = (event) => {
    //         event = event || window.event;
    //         event.preventDefault();
    
    //         pos3 = event.clientX;
    //         pos4 = event.clientY;
    //         document.onmouseup = closeDragElement;
    //         document.onmousemove = dragElement;
    //     }

    //     const dragElement = (event) => {
    //         event = event || window.event;
    //         event.preventDefault();

    //         pos1 = pos3 - event.clientX;
    //         pos2 = pos4 - event.clientY;
    //         pos3 = event.clientX;
    //         pos4 = event.clientY;

    //         element.style.top = (element.offsetTop - pos2) + 'px';
    //         element.style.left = (element.offsetLeft - pos1) + 'px';
    //     }

    //     const closeDragElement = () => {
    //         document.onmouseup = null;
    //         document.onmousemove = null;
    //     }
    
    //     header.onmousedown = dragMouseDown;
    //     header.ontouchstart = dragMouseDown;
    // }

    const dragMove = (element: HTMLElement, header: HTMLElement) => {
        header.style.cursor = 'grab';
        let pos1 = 0, pos2 = 0, pos3 = 0, pos4 = 0;
    
        const dragMouseDown = (event) => {
            event.preventDefault();
    
            if (event.type === 'mousedown') {
                pos3 = event.clientX;
                pos4 = event.clientY;
                document.onmousemove = dragElement;
                document.onmouseup = closeDragElement;
            } else if (event.type === 'touchstart') {
                pos3 = event.touches[0].clientX;
                pos4 = event.touches[0].clientY;
                document.ontouchmove = dragElement;
                document.ontouchend = closeDragElement;
            }
        };
    
        const dragElement = (event) => {
            event.preventDefault();
    
            if (event.type === 'mousemove') {
                pos1 = pos3 - event.clientX;
                pos2 = pos4 - event.clientY;
                pos3 = event.clientX;
                pos4 = event.clientY;
            } else if (event.type === 'touchmove') {
                pos1 = pos3 - event.touches[0].clientX;
                pos2 = pos4 - event.touches[0].clientY;
                pos3 = event.touches[0].clientX;
                pos4 = event.touches[0].clientY;
            }
    
            if (pos3 > 0 && pos4 > 0 && pos3 < window.innerWidth && pos4 < window.innerHeight) {
                element.style.top = (element.offsetTop - pos2) + 'px';
                element.style.left = (element.offsetLeft - pos1) + 'px';
            }
            else {
                document.onmouseup(event);
                closeDragElement();
            }
        };
    
        const closeDragElement = () => {
            document.onmouseup = null;
            document.onmousemove = null;
            document.ontouchend = null;
            document.ontouchmove = null;
        };
    
        header.onmousedown = (event) => {
            if (event.target instanceof Element && (event.target.closest('.bcaReview__popupHeaderIcon') || event.target.closest('.bcaReview__popupHeaderFull'))) {
                return;
            }
            dragMouseDown(event);
        };
    
        header.ontouchstart = (event) => {
            if (event.target instanceof Element && (event.target.closest('.bcaReview__popupHeaderIcon') || event.target.closest('.bcaReview__popupHeaderFull'))) {
                return;
            }
            dragMouseDown(event);
        };
    };

    useEffect(() => {
        axios.get(`tests/digital/bca/review/${testId}/`)
            .then(res => {
                // console.log(res.data);
                setStudentName(res.data.studentName);
                setTestModules(res.data.testModules);
            })
            .catch(error => {
                // console.log(error);
                dispatch(setError(error));
            });
    }, []);

    useEffect(() => {
        if (testModules) {
            setShortcutKeyPressed(true);

            // dragMove(document.getElementById('shortcutsDrag'), document.getElementById('shortcutsHeader'));
            const shortcutsDragEl = document.getElementById('shortcutsDrag');
            const shortcutsHeaderEl = document.getElementById('shortcutsHeader');
            if (shortcutsDragEl && shortcutsHeaderEl) {
                dragMove(shortcutsDragEl, shortcutsHeaderEl);
            }

            const explanationDragEl = document.getElementById('explanationDrag');
            const explanationHeaderEl = document.getElementById('explanationHeader');
            if (explanationDragEl && explanationHeaderEl) {
                dragMove(explanationDragEl, explanationHeaderEl);
            }
        }
    }, [testModules]);

    useLayoutEffect(() => {
        if (testModules) {
            renderKatex();
        }
    }, [testModules, moduleIndex, questionIndex]);

    // useEffect(() => {
    //     if (explanationVideo) {
    //         setPlayer(new Player('vimeoPlayer', {
    //             url: explanationVideo
    //         }));
    //     }
    //     else {
    //         setPlayer(null);
    //     }
    // }, [explanationVideo]);

    useEffect(() => {
        if (testModules) {
            renderKatex();
        }
    }, [directionsOpen, explanationOpen]);

    useEffect(() => {
        if (getDeviceOS() === 'windows') {
            document.addEventListener('keydown', handleWindowsShortcutKeys);
        }
        else if (getDeviceOS() === 'mac_os') {
            document.addEventListener('keydown', handleMacShortcutKeys);
        }

        return () => {
            if (getDeviceOS() === 'windows') {
                document.removeEventListener('keydown', handleWindowsShortcutKeys);
            }
            else if (getDeviceOS() === 'mac_os') {
                document.removeEventListener('keydown', handleMacShortcutKeys);
            }
        }
    }, [shortcutKeyPressed]);

    if (testModules === null) return <Loader />;

    const {
        name,
        questions,
        answers,
        subject,
        prompt
    } = testModules[moduleIndex];

    return (
        <div className='bcaReviewBox' onCopy={(event) => { event.preventDefault() }}>
            {/* {explanationVideo &&
                <Popup
                    secondaryButtonText='CLOSE'
                    secondaryButtonOnClick={() => setExplanationVideo(null)}
                    innerRef={explanationRef}
                >
                    <div className='bcaReview__videoContainer'>
                        <div id='vimeoPlayer'></div>
                    </div>
                </Popup>
            } */}
            <Helmet>
                <title>
                    Reviewing {name} - {organization.name}
                </title>
            </Helmet>
            <div className='bcaReview'>
                <div className='bcaReview__header'>
                    <div className='bcaReview__titleGroup'>
                        <button className='bcaReview__moduleSelect' onClick={() => setModuleSelectOpen(!moduleSelectOpen)}>
                            <h1 className='bcaReview__title'>
                                {name}
                            </h1>
                            {moduleSelectOpen
                                ? <MdKeyboardArrowUp className='bcaReview__moduleSelectIcon' />
                                : <MdKeyboardArrowDown className='bcaReview__moduleSelectIcon' />
                            }
                            {moduleSelectOpen &&
                                <div className='bcaReview__moduleSelectDropdown' ref={moduleSelectRef}>
                                    {testModules.map((testModule, t) => (
                                        <p
                                            className={t === moduleIndex
                                                ? 'bcaReview__moduleSelectDropdownItem--selected'
                                                : 'bcaReview__moduleSelectDropdownItem'
                                            }
                                            onClick={() => {
                                                if (t !== moduleIndex) {
                                                    setModuleIndex(t);
                                                    setQuestionIndex(0);
                                                }
                                            }}
                                        >
                                            {testModule.name}
                                        </p>
                                    ))}
                                </div>
                            }
                        </button>
                        <button className='bcaReview__directionsButton' onClick={() => setDirectionsOpen(!directionsOpen)}>
                            {directionsOpen
                                ? <u>Directions</u>
                                : <p>Directions</p>
                            }
                            {directionsOpen
                                ? <MdKeyboardArrowUp className='bcaReview__directionsIcon' />
                                : <MdKeyboardArrowDown className='bcaReview__directionsIcon' />
                            }
                        </button>
                    </div>
                    <div className='bcaReview__timerGroup'>
                        <p className='bcaReview__timer'>
                            {formatTime(answers[questionIndex].timeSpent)}
                        </p>
                        <p className='bcaReview__timerText'>
                            Time spent
                        </p>
                    </div>
                    <div className='bcaReview__actionGroup'>
                        <button
                            className={shortcutsOpen
                                ? 'bcaReview__action--active'
                                : 'bcaReview__action'
                            }
                            onClick={() => {
                                setShortcutsOpen(!shortcutsOpen);
                            }}
                        >
                            <MdOutlineKeyboard className='bcaReview__actionIcon' />
                            <p className='bcaReview__actionText'>Shortcuts</p>
                        </button>
                        <button
                            className='bcaReview__action'
                            onClick={() => navigate(-1)}
                            // onClick={() => navigate(`/report/digital-bca/${testId}`)}
                        >
                            <MdExitToApp className='bcaReview__actionIcon' />
                            <p className='bcaReview__actionText'>Exit</p>
                        </button>
                    </div>
                    {directionsOpen &&
                        <div className='bcaReview__directions' ref={directionsRef}>
                            <div className='bcaReview__directionsBubble'>
                                <p className='katex bcaReview__directionsText'>
                                    {prompt
                                        ? prompt
                                        : 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Duis sollicitudin, mi congue condimentum dignissim, ipsum diam tristique velit, et rutrum orci massa non purus. Etiam vitae ante sed velit blandit condimentum. Donec lectus ipsum, volutpat vel tortor a, blandit elementum felis. Mauris a ultricies quam, at molestie ipsum. Cras nec ante justo. Suspendisse egestas lectus sit amet sem maximus auctor. In ut dignissim lorem, ac tristique eros. Nullam eget sollicitudin quam. Quisque pellentesque cursus justo sed ornare. Integer finibus, tortor at facilisis molestie, ligula augue cursus ipsum, eget auctor enim sem a eros. Pellentesque habitant morbi tristique senectus et netus et malesuada fames ac turpis egestas. Pellentesque suscipit, nunc eget mollis consequat, augue odio suscipit enim, vel varius eros leo sit amet magna. Nunc suscipit leo et metus bibendum, a finibus libero finibus. In lacinia scelerisque urna ut pretium. In at est aliquam, dictum odio non, fringilla tellus. Vivamus commodo molestie urna at dapibus. Nulla facilisi. Maecenas sit amet lorem tincidunt, maximus nunc eu, malesuada velit. Class aptent taciti sociosqu ad litora torquent per conubia nostra, per inceptos himenaeos. Nulla facilisi.'
                                    }
                                </p>
                                <Button
                                    className='bcaReview__directionsClose'
                                    onClick={() => setDirectionsOpen(false)}
                                    secondary
                                >
                                    CLOSE
                                </Button>
                            </div>
                        </div>
                    }
                    <div id='shortcutsDrag' className={shortcutsOpen
                        ? 'bcaReview__popup--shortcuts'
                        : 'bcaReview__popup--hidden'
                    }>
                        <div id='shortcutsHeader' className='bcaReview__popupHeader'>
                            <h3 className='bcaReview__popupHeaderText'>
                                Keyboard shortcuts{getDeviceOS() === 'windows'
                                    ? ': Windows'
                                    : getDeviceOS() === 'mac_os'
                                    ? ': MacOS'
                                    : ''
                                }
                            </h3>
                            <MdClose className='bcaReview__popupHeaderIcon' onClick={() => setShortcutsOpen(false)} />
                        </div>
                        <Shortcuts />
                    </div>
                    {(organization.customizations.find(customization => customization === 'Show Explanation') && questions[questionIndex].explanation) &&
                        <div id='explanationDrag' className={explanationOpen
                            ? 'bcaReview__popup--explanation'
                            : 'bcaReview__popup--hidden'

                        }>
                            <div id='explanationHeader' className='bcaReview__popupHeader'>
                                <h3 className='bcaReview__popupHeaderText'>
                                    Explanation
                                </h3>
                                <MdClose className='bcaReview__popupHeaderIcon' onClick={() => setExplanationOpen(false)} />
                            </div>
                            <div className='bcaReview__popupBody'>
                                <p className='katex bcaReview__popupBodyText'>
                                    {questions[questionIndex].explanation}
                                </p>
                            </div>
                        </div>
                    }
                </div>
                <div className='bcaReview__dottedLine'></div>
                <div className='bcaReview__test'>
                    <div className='bcaReview__question'>
                        <div className='bcaReview__questionContent'>
                            <div className='bcaReview__questionHeader'>
                                <p className={answers[questionIndex].isCorrect
                                    ? 'bcaReview__questionNumber--correct'
                                    : 'bcaReview__questionNumber--wrong'
                                }>
                                    {questionIndex + 1}
                                </p>
                                <button className='bcaReview__questionMarkButton' onClick={() => {}}>
                                    {answers[questionIndex].marked
                                        ? <MdOutlineBookmark className='bcaReview__questionMarkIcon--marked' />
                                        : <MdOutlineBookmarkBorder className='bcaReview__questionMarkIcon' />
                                    }
                                    Mark for review
                                </button>
                                {(organization.customizations.find(customization => customization === 'Show Explanation') && questions[questionIndex].explanation) &&
                                    <button
                                        className={explanationOpen
                                            ? 'bcaReview__showExplanation--active'
                                            : 'bcaReview__showExplanation'
                                        }
                                        onClick={() => setExplanationOpen(!explanationOpen)}
                                    >
                                        Explanation
                                    </button>
                                }
                                {organization.customizations.find(customization => customization === 'Enable Show Answer') &&
                                    <button
                                        className={showAnswer
                                            ? 'bcaReview__showAnswer--active'
                                            : 'bcaReview__showAnswer'
                                        }
                                        onClick={() => setShowAnswer(!showAnswer)}
                                    >
                                        {showAnswer ? 'Hide answer' : 'Show answer'}
                                    </button>
                                }
                            </div>
                            <div id='bcaReview__questionChoices' className='bcaReview__questionChoices'>
                                {questions[questionIndex].figures.map(figure => (
                                        <ToolErrorBoundary
                                            key={figure.id}
                                            fallback={
                                                <img
                                                    className='bcaReview__figure'
                                                    src={figure.image}
                                                    alt={(questionIndex + 1).toString()}
                                                    draggable={false}
                                                />
                                            }
                                        >
                                            <FigureViewer
                                                src={figure.image}
                                                alt={(questionIndex + 1).toString()}
                                                imgClassName='bcaReview__figure'
                                            />
                                        </ToolErrorBoundary>
                                    ))}
                                <p className='bcaReview__questionQuestion'>
                                    <span className='katex'>
                                        {questions[questionIndex].question}
                                    </span>
                                </p>
                                {questions[questionIndex].choices.length
                                ?   <>
                                        {questions[questionIndex].choices.map((choice, i) => (
                                            <div key={choice.id} className='bcaReview__questionChoiceWrapper'>
                                                <button
                                                    className={showAnswer && choice.isCorrect
                                                        ? 'bcaReview__questionChoice--correct'
                                                        : answers[questionIndex].answer == choice.id
                                                        ? (choice.isCorrect 
                                                            ? 'bcaReview__questionChoice--selected'
                                                            : 'bcaReview__questionChoice--wrong'
                                                        )
                                                        : 'bcaReview__questionChoice'
                                                    }
                                                    onClick={() => {}}
                                                >
                                                    <div className='bcaReview__questionChoiceOutline'></div>
                                                    <p className='bcaReview__questionChoiceLetter'>
                                                        {MULTIPLE_CHOICES[i]}
                                                    </p>
                                                    {choice.figure
                                                    ?   <img
                                                            className='bcaReview__questionChoiceFigure'
                                                            key={choice.figure}
                                                            src={choice.figure}
                                                            alt={choice.figure}
                                                            draggable={false}
                                                        />
                                                    :   <span className='bcaReview__questionChoiceTextWrapper'>
                                                            <p className='katex bcaReview__questionChoiceText'>
                                                                {choice.choice}
                                                            </p>
                                                        </span>
                                                    }
                                                </button>
                                            </div>
                                        ))}
                                    </>
                                :   null
                                }
                                <div className='bcaReview__marginBottom'></div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className='bcaReview__dottedLine'></div>
                <div className='bcaReview__footer'>
                    {/* {questions[questionIndex].explanationVideo
                    ?   <div className='bcaReview__buttons'>
                            <Button
                                className='bcaReview__explanationButton'
                                onClick={() => setExplanationVideo(questions[questionIndex].explanationVideo)}
                            >
                                SOLUTION VIDEO
                                <MdOutlinePlayCircle className='bcaReview__explanationButtonIcon' />
                            </Button>
                        </div>
                    :    <p className='bcaReview__tester'>
                            {studentName}
                        </p>
                    } */}
                    <p className='bcaReview__tester'>
                        {studentName}
                    </p>
                    <button
                        className='bcaReview__navigatorToggle'
                        onClick={() => setNavigatorOpen(!navigatorOpen)}
                    >
                        Question {questionIndex + 1}/{questions.length}
                        {navigatorOpen
                            ? <MdKeyboardArrowDown className='bcaReview__navigatorToggleIcon' />
                            : <MdKeyboardArrowUp className='bcaReview__navigatorToggleIcon' />
                        }
                    </button>
                    <div className='bcaReview__buttons'>
                        <Button
                            className='bcaReview__button'
                            secondary
                            disabled={moduleIndex === 0 && questionIndex === 0}
                            onClick={prevQuestion}
                        >
                            BACK
                        </Button>
                        <Button
                            className='bcaReview__button'
                            onClick={nextQuestion}
                        >
                            NEXT
                        </Button>
                    </div>
                    {navigatorOpen &&
                        <div className='bcaReview__navigator' ref={navigatorRef}>
                            <div className='bcaReview__navigatorBubble'>
                                <h2 className='bcaReview__navigatorTitle'>
                                    {name} Questions
                                </h2>
                                <div className='bcaReview__navigatorLegends'>
                                    <div className='bcaReview__navigatorLegend'>
                                        <MdOutlineLocationOn className='bcaReview__navigatorLegendCurrent' />
                                        <p className='bcaReview__navigatorLegendText'>
                                            Current
                                        </p>
                                    </div>
                                    <div className='bcaReview__navigatorLegend'>
                                        <div className='bcaReview__navigatorLegendCorrect'></div>
                                        <p className='bcaReview__navigatorLegendText'>
                                            Correct
                                        </p>
                                    </div>
                                    <div className='bcaReview__navigatorLegend'>
                                        <div className='bcaReview__navigatorLegendWrong'></div>
                                        <p className='bcaReview__navigatorLegendText'>
                                            Incorrect
                                        </p>
                                    </div>
                                    <div className='bcaReview__navigatorLegend'>
                                        <MdOutlineBookmark className='bcaReview__navigatorLegendMarked' />
                                        <p className='bcaReview__navigatorLegendText'>
                                            For review
                                        </p>
                                    </div>
                                </div>
                                <div className='bcaReview__navigatorItems'>
                                    {questions.map((question, i) => (
                                        <button
                                            key={`${question.id}nav`}
                                            className={answers[i].isCorrect
                                                ? 'bcaReview__navigatorItem--correct'
                                                : 'bcaReview__navigatorItem--wrong'
                                            }
                                            onClick={() => {
                                                goToQuestion(i);
                                                setNavigatorOpen(false);
                                            }}
                                        >
                                            {questionIndex === i
                                                ? <u className='bcaReview__navigatorItemNumber'>{i + 1}</u>
                                                : <p className='bcaReview__navigatorItemNumber'>{i + 1}</p>
                                            }
                                            {questionIndex === i && <MdOutlineLocationOn className='bcaReview__navigatorItemCurrent' />}
                                            {answers[i].marked && <MdOutlineBookmark className='bcaReview__navigatorItemMarked' />}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    }
                </div>
            </div>
        </div>
    );
}

export default DigitalBCAReview;